import { LeafTypes, NavigableNodeTypes, NodeType } from 'shared/types/ingestion'
import {
  appendRowsToSpreadsheet,
  highlightSpreadsheetColumns,
  createSpreadsheet,
  setSpreadsheetWrapStrategy,
  GDriveValue,
  WrapStrategy,
} from 'infrastructure/googleDrive'
import { GDriveSpreadsheet } from '../googleApis/types'
import { ILeafRequest, INode } from 'shared/types/tree'
import { directTreeAccessUrl } from 'infrastructure/api'
import {
  allLeavesOfType,
  childNodesOfType,
  leavesOfType,
} from 'components/utility/TreeHelpers'
import {
  getStandardsMapFromIds,
  normalizeStandardName,
} from 'components/tree/standardUtilities'
import { Standard } from 'shared/types'
import { createIdMap, NodeIdMap } from 'components/version_mapping/utility'
import { bulkCreateLeavesFromList } from '../TreeActions'
import { closestNavigableParent } from 'components/utility/TreeNavigation'

function stripHTML(html: string) {
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html
  return tempDiv.innerText
}

export async function createRowsFromKiddomTree(
  tree: INode,
): Promise<string[][]> {
  const headers: string[] = [
    'Addressing Standards to add',
    'Existing Standards',
    'node id',
    'link to node',
  ]
  const rows: string[][] = []

  const allUniqueStandardIDs = new Set(
    allLeavesOfType(tree, LeafTypes.Skill).map((s) => {
      return s.body.skillID
    }),
  )
  const standardsMap = await getStandardsMapFromIds(
    Array.from(allUniqueStandardIDs),
  )
  const idMap = createIdMap(tree)

  let currentLevel = 1
  function recursivelyMakeRows(node: INode, ancestors: string[] = []) {
    if (
      (node.title && NavigableNodeTypes.includes(node.type as NodeType)) ||
      node.type === NodeType.Question
    ) {
      const parent = closestNavigableParent(node, idMap)

      const linkToNode = directTreeAccessUrl({
        treeId: node.tree_id,
        versionId: node.version,
        nodeId: parent.id,
      })

      const existingStandardLists = childNodesOfType(node, NodeType.SkillsList)
      const existingStandardIDs = existingStandardLists
        .flatMap((list) => leavesOfType(list, LeafTypes.Skill))
        .map((s) => s.body.skillID)
      const existingStandards = existingStandardIDs
        .map((id) => standardsMap[id])
        .join(', ')

      const nodeText =
        node.type === NodeType.Question && node.description
          ? stripHTML(node.description).substring(0, 100)
          : node.title
      const newRow = [
        '',
        existingStandards,
        node.id,
        linkToNode,
        ...ancestors,
        nodeText,
      ]
      if (newRow.length > headers.length) {
        headers.push(`Level ${currentLevel}`)
        currentLevel++
      }
      rows.push(newRow)
    }

    node.nodes?.forEach((n) => {
      recursivelyMakeRows(n, [...ancestors, node.title])
    })
  }

  recursivelyMakeRows(tree)

  return [headers, ...rows]
}

export async function exportKiddomTreeToSpreadsheet(
  tree: INode,
  accessToken: string,
): Promise<string> {
  const spreadsheetId = await createSpreadsheet(
    accessToken,
    `${tree.title}-standards`,
  )
  const rows = await createRowsFromKiddomTree(tree)
  await appendRowsToSpreadsheet(accessToken, spreadsheetId, rows)
  await highlightSpreadsheetColumns(accessToken, spreadsheetId, [0])
  await setSpreadsheetWrapStrategy(
    accessToken,
    spreadsheetId,
    WrapStrategy.CLIP,
  )
  return spreadsheetId
}

export type NodeAndStandards = {
  nodeId: string
  nodeTitle: string
  linkToNode: string
  standardsListId?: string
  standards: Standard[]
  issues: string[]
}

function getLastPopulatedCell(row: GDriveValue[]) {
  let lastIndex = row.length
  let value = ''
  while (value === '' && lastIndex > 0) {
    lastIndex -= 1
    value = row[lastIndex].formattedValue || ''
  }
  return row[lastIndex]
}

function getStandardsListForNode(node: INode): INode | undefined {
  const standardsLists = childNodesOfType(node, NodeType.SkillsList)
  if (node.type === NodeType.Question) {
    return standardsLists[0]
  }
  return standardsLists.find((list) =>
    list.title.toUpperCase().includes('ADDRESSING'),
  )
}

export function processSpreadsheet(
  spreadsheet: GDriveSpreadsheet,
  stateStandardsMap: Record<string, Standard>,
  treeMap: NodeIdMap,
): NodeAndStandards[] {
  const spreadsheetRows = spreadsheet.sheets[0].getRows()
  const headerIndices = spreadsheet.sheets[0].getIndicesOfStringsInRow(0, [
    'Addressing Standards to add',
    'node id',
    'link to node',
  ])
  spreadsheetRows.shift()

  // Create a map of standards by ID for deduplication lookups
  const stateStandardsById = Object.values(stateStandardsMap).reduce<
    Record<string, Standard>
  >((acc, standard) => {
    if (standard.standardId) {
      acc[standard.standardId] = standard
    }
    return acc
  }, {})

  // Create a set of existing standards for each node
  const existingStandardsByNode = new Map<string, Set<string>>()
  for (const row of spreadsheetRows) {
    const nodeId = row[headerIndices['node id']].formattedValue
    if (nodeId && treeMap[nodeId]) {
      const node = treeMap[nodeId].node
      const skillsLists = childNodesOfType(node, NodeType.SkillsList).filter(
        (list) => list.title.toUpperCase().includes('ADDRESSING'),
      )

      const existingStandards = new Set<string>()

      for (const list of skillsLists) {
        const skills = leavesOfType(list, LeafTypes.Skill)
        for (const skill of skills) {
          if (skill.body.skillID) {
            const standard = stateStandardsById[skill.body.skillID]
            if (standard) {
              // Store normalized name of existing standard
              existingStandards.add(
                normalizeStandardName(standard.standardName),
              )
            }
          }
        }
      }
      existingStandardsByNode.set(nodeId, existingStandards)
    }
  }

  return spreadsheetRows.reduce<NodeAndStandards[]>((ns, row) => {
    const standardList =
      row[headerIndices['Addressing Standards to add']].formattedValue
    const nodeId = row[headerIndices['node id']].formattedValue
    const nodeTitle = getLastPopulatedCell(row).formattedValue || ''
    const linkToNode = row[headerIndices['link to node']].formattedValue
    if (!standardList || !nodeId || !linkToNode) return ns

    const existingStandards =
      existingStandardsByNode.get(nodeId) || new Set<string>()
    const addedStandards = new Set<string>() // Track standards added in this row

    const standardsAndIssues = standardList
      .split(/[;,]/)
      .map((standard) => standard.trim())
      .filter((standard) => standard.length > 0)
      .reduce<{ standards: Standard[]; issues: string[] }>(
        (acc, standard) => {
          const normalizedName = normalizeStandardName(standard)

          // Check if standard already exists in node
          if (existingStandards.has(normalizedName)) {
            acc.issues.push(`${standard} already exists for this node`)
            return acc
          }

          // Check if standard was already added in this list
          if (addedStandards.has(normalizedName)) {
            acc.issues.push(`${standard} appears multiple times in the list`)
            return acc
          }

          // Find matching standard by normalized name
          const matchingStandard = stateStandardsMap[normalizedName]

          if (matchingStandard) {
            acc.standards.push(matchingStandard)
            addedStandards.add(normalizedName)
          } else {
            acc.issues.push(
              `${standard} not found in Kiddom Standards Database`,
            )
          }

          return acc
        },
        { standards: [], issues: [] },
      )

    let standardsList
    if (!treeMap[nodeId]) {
      standardsAndIssues.issues.push(`Node not found in Kiddom`)
    } else {
      standardsList = getStandardsListForNode(treeMap[nodeId].node)
      if (!standardsList) {
        standardsAndIssues.issues.push(`Standards List not found in Kiddom`)
      }
    }

    ns.push({
      nodeId,
      nodeTitle,
      linkToNode,
      standardsListId: standardsList?.id,
      ...standardsAndIssues,
    })
    return ns
  }, [])
}

export function createLeaves(
  tree: INode,
  nodesAndStandards: NodeAndStandards[],
) {
  const leafRequests: ILeafRequest[] = nodesAndStandards.flatMap((ns) => {
    return ns.standards.map((s) => {
      return {
        parent_id: ns.standardsListId,
        type: LeafTypes.Skill,
        body: {
          skillID: s.standardId,
        },
      }
    })
  })
  return bulkCreateLeavesFromList({
    treeId: tree.tree_id,
    versionId: tree.version,
    leaves: leafRequests,
  })
}
